import { t } from "./i18n";

// one place that makes every sheet, modal and popover behave like a dialog for keyboards and screen readers:
// it gets role and label, focus moves in when it opens, Tab stays inside, Escape closes it and focus goes back
// to whatever opened it. nothing here knows about a particular sheet, it watches the page for them (.modal,
// [role=dialog] and the who sheet), so views keep toggling `hidden` as they always did. Back button layers
// stay in history_stack.ts, this only handles focus

const DIALOGS = ".modal, [role='dialog']";
const FOCUSABLE = "a[href], button, input, select, textarea, summary, [tabindex], [contenteditable='true']";
// sheets that close themselves on Escape, so the fallback below leaves them alone
const OWN_ESCAPE = new Set(["note-sheet", "oc-editor", "oc-merge", "mu-track-sheet", "mu-pl-sheet", "tl-tier-sheet"]);

interface Open {
  el: HTMLElement;
  opener: HTMLElement | null;
}

const stack: Open[] = [];
let lastOutside: HTMLElement | null = null;
let labelCount = 0;

function isOpen(el: HTMLElement): boolean {
  return el.isConnected && !el.closest("[hidden], [aria-hidden='true']");
}

function openAncestor(node: Element): HTMLElement | null {
  const dialog = node.closest<HTMLElement>(DIALOGS);
  return dialog && isOpen(dialog) ? dialog : null;
}

export function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    if ((el as HTMLButtonElement).disabled || el.tabIndex < 0 || el.closest("[hidden], [inert]")) return false;
    if (el instanceof HTMLInputElement && el.type === "hidden") return false;
    return el.getClientRects().length > 0;
  });
}

function label(el: HTMLElement): void {
  if (!el.hasAttribute("role") && !el.matches("#who-sheet")) el.setAttribute("role", "dialog");
  if (el.getAttribute("role") === "dialog" && !el.hasAttribute("aria-modal")) el.setAttribute("aria-modal", "true");
  if (el.hasAttribute("aria-label") || el.hasAttribute("aria-labelledby")) return;
  const heading = el.querySelector<HTMLElement>("h1, h2, h3, [role='heading']");
  if (heading) {
    heading.id ||= `dlg-title-${++labelCount}`;
    el.setAttribute("aria-labelledby", heading.id);
  } else if (!el.matches("#who-sheet")) {
    el.setAttribute("aria-label", t("dialog"));
  }
}

function opened(el: HTMLElement): void {
  if (stack.some((entry) => entry.el === el)) return;
  label(el);
  const opener = lastOutside?.isConnected ? lastOutside : null;
  stack.push({ el, opener });
  if (el.contains(document.activeElement) && document.activeElement !== el) return;
  const target = el.querySelector<HTMLElement>("[autofocus]") ?? focusables(el).find((item) => !item.matches(".sheet-grip")) ?? null;
  if (target) target.focus();
  else {
    if (!el.hasAttribute("tabindex")) el.tabIndex = -1;
    el.focus({ preventScroll: true });
  }
}

function closed(entry: Open): void {
  stack.splice(stack.indexOf(entry), 1);
  const active = document.activeElement;
  const lost = !active || active === document.body || entry.el.contains(active);
  if (lost && entry.opener?.isConnected && !entry.opener.closest("[hidden]")) entry.opener.focus({ preventScroll: true });
}

function sync(): void {
  for (const entry of [...stack]) if (!isOpen(entry.el)) closed(entry);
  for (const el of document.querySelectorAll<HTMLElement>(DIALOGS)) if (isOpen(el)) opened(el);
  // a closed who sheet is still in the tab order, take it out of reach
  const who = document.getElementById("who-sheet");
  if (who) who.toggleAttribute("inert", who.getAttribute("aria-hidden") === "true");
}

function trap(event: KeyboardEvent): void {
  const top = stack[stack.length - 1];
  if (!top || event.key !== "Tab") return;
  const items = focusables(top.el);
  if (!items.length) {
    event.preventDefault();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (!active || !top.el.contains(active)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

// sheets with no Escape handling of their own: give them their close or cancel key
function escape(event: KeyboardEvent): void {
  const top = stack[stack.length - 1];
  if (!top || event.key !== "Escape" || event.defaultPrevented || OWN_ESCAPE.has(top.el.id) || top.el.closest("#who-sheet")) return;
  window.setTimeout(() => {
    if (!isOpen(top.el) || stack[stack.length - 1] !== top) return;
    top.el.querySelector<HTMLButtonElement>("[id$='close'], [id$='cancel']")?.click();
  }, 0);
}

export function setupModalA11y(): void {
  // remember where focus was before a dialog took it, a press counts too since safari never focuses buttons on click
  const remember = (target: EventTarget | null) => {
    if (target instanceof HTMLElement && !openAncestor(target) && target !== document.body) lastOutside = target.closest<HTMLElement>(FOCUSABLE) ?? target;
  };
  document.addEventListener("focusin", (event) => remember(event.target), true);
  document.addEventListener("pointerdown", (event) => remember(event.target), true);
  document.addEventListener("keydown", trap, true);
  document.addEventListener("keydown", escape);
  const relevant = (node: Node) => node instanceof Element && (node.matches(DIALOGS) || node.querySelector(DIALOGS) !== null);
  new MutationObserver((records) => {
    if (records.some((r) => (r.type === "attributes" ? r.target instanceof Element && (r.target.matches(DIALOGS) || r.target.id === "who-sheet" || stack.length > 0 || r.target.querySelector(DIALOGS) !== null) : [...r.addedNodes, ...r.removedNodes].some(relevant)))) sync();
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["hidden", "aria-hidden"] });
  sync();
}
